import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import './ALCChart.css';
import useChartDragZoom from '../useChartDragZoom';
import useIsNarrowScreen from '../useIsNarrowScreen';
import { downloadChartImage, getAutoScaleFromData } from '../downloadChartImage';
import {
    pickSpanModeForScreen,
    createTickLabelFormatter,
    createFixedGridTicksPlugin,
    createTimeScaleConfig,
    niceStep,
} from '../timeScaleUtils';
import { crosshairYPlugin } from '../../../plugins';

const CHART_CONSTANTS = {
    COLORS: {
        main: '#3B82F6',
        warning: '#FFA500',
        action: '#FF0000',
        grid: 'rgba(156, 163, 175, 0.2)',
        gridStrong: 'rgba(156, 163, 175, 0.6)',
        gridBorder: 'rgba(156, 163, 175, 0.4)',
    },
    LABELS: {
        yAxis: '地錨荷重 (Ton)',
        warningUpper: '警戒上限',
        warningLower: '警戒下限',
        actionUpper: '行動上限',
        actionLower: '行動下限',
    },
};

const THRESHOLD_CONFIGS = [
    {
        key: 'warning_high',
        label: CHART_CONSTANTS.LABELS.warningUpper,
        color: CHART_CONSTANTS.COLORS.warning,
        dash: [5, 5],
        legendGroup: 'warning',
        legendLabel: '警戒值',
    },
    {
        key: 'warning_low',
        label: CHART_CONSTANTS.LABELS.warningLower,
        color: CHART_CONSTANTS.COLORS.warning,
        dash: [5, 5],
        legendGroup: 'warning',
        legendLabel: '警戒值',
    },
    {
        key: 'action_high',
        label: CHART_CONSTANTS.LABELS.actionUpper,
        color: CHART_CONSTANTS.COLORS.action,
        dash: [10, 5],
        legendGroup: 'action',
        legendLabel: '行動值',
    },
    {
        key: 'action_low',
        label: CHART_CONSTANTS.LABELS.actionLower,
        color: CHART_CONSTANTS.COLORS.action,
        dash: [10, 5],
        legendGroup: 'action',
        legendLabel: '行動值',
    },
];

// ─────────────────────────────────────────────────────────────────────────────

function computeYAxisConfig(values) {
    const numericValues = (values || []).filter(
        (v) => typeof v === 'number' && !Number.isNaN(v)
    );
    if (numericValues.length === 0) return { min: 0, max: 10, stepSize: 1 };

    let dataMin = Infinity;
    let dataMax = -Infinity;
    for (const v of numericValues) {
        if (v < dataMin) dataMin = v;
        if (v > dataMax) dataMax = v;
    }
    const range = dataMax - dataMin || 1;

    const roughStep = range / 10;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep));
    const normalizedStep = roughStep / magnitude;

    let stepSize;
    if (normalizedStep <= 1) stepSize = 1 * magnitude;
    else if (normalizedStep <= 2) stepSize = 2 * magnitude;
    else if (normalizedStep <= 5) stepSize = 5 * magnitude;
    else stepSize = 10 * magnitude;

    stepSize =
        stepSize < 1
            ? Math.max(0.1, Math.round(stepSize * 10) / 10)
            : Math.max(1, Math.round(stepSize));

    const suggestedMin = Math.floor(dataMin / stepSize) * stepSize;
    const suggestedMax = Math.ceil(dataMax / stepSize) * stepSize;
    const padding = stepSize * 0.3;

    const min = suggestedMin - padding;
    const max = suggestedMax + padding;

    return {
        min: min === max ? min - stepSize : min,
        max: min === max ? max + stepSize : max,
        stepSize,
    };
}

const ALCChart = ({ measurements = [], deviceName, slopeData, deviceId }) => {
    const { points, thresholds, yAxisConfig, spanMs, actionUp, maxValue } = useMemo(() => {
        const sorted = (measurements || [])
            .slice()
            .sort((a, b) => new Date(a.obs_time) - new Date(b.obs_time));

        const pts = sorted
            .map((item) => {
                const dt = DateTime.fromISO(item.obs_time).startOf('minute');
                if (!dt.isValid) return null;
                return { x: dt.toMillis(), y: Number(item.value) || 0 };
            })
            .filter(Boolean);

        if (pts.length === 0) {
            return {
                points: [],
                thresholds: [],
                yAxisConfig: computeYAxisConfig([]),
                spanMs: 0,
                maxValue: null,
            };
        }

        const deviceData = slopeData?.force?.find((d) => d.id === deviceId);
        const values = pts.map((p) => p.y);
        const allValues = [...values];

        const mkConstSeries = (value) =>
            pts.map((p) => ({
                x: p.x,
                y: Number(value),
            }));

        const thresholdDatasets = [];
        THRESHOLD_CONFIGS.forEach(
            ({ key, label, color, dash, legendGroup, legendLabel }) => {
                if (deviceData?.[key] != null) {
                    const num = Number(deviceData[key]);
                    allValues.push(num);
                    thresholdDatasets.push({
                        label,
                        data: mkConstSeries(num),
                        borderColor: color,
                        backgroundColor: 'transparent',
                        // borderDash: dash,
                        borderWidth: 1.5,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        yAxisID: 'y',
                        parsing: false,
                        _isThreshold: true,
                        legendGroup,
                        legendLabel,
                    });
                }
            }
        );

        const xs = pts.map((p) => p.x).sort((a, b) => a - b);
        const spanMs = xs.length >= 2 ? xs[xs.length - 1] - xs[0] : 0;
        const yAxisConfig = computeYAxisConfig(allValues);

        // 取得行動值上限（key 是 action_high）
        const actionUp =
            deviceData?.action_high != null ? Number(deviceData.action_high) : null;

        const maxValue = allValues.reduce(
            (currentMax, value) =>
                typeof value === 'number' && Number.isFinite(value) && value > currentMax
                    ? value
                    : currentMax,
            -Infinity
        );

        return {
            points: pts,
            thresholds: thresholdDatasets,
            yAxisConfig,
            spanMs,
            actionUp,
            maxValue: Number.isFinite(maxValue) ? maxValue : null,
        };
    }, [measurements, slopeData, deviceId]);

    const chartTitleText = [deviceName, '地錨荷重歷史趨勢'].filter(Boolean).join(' ');
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(800);

    // 監聽容器寬度變化
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateWidth = () => {
            setChartWidth(container.clientWidth || 800);
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    const isNarrowScreen = useIsNarrowScreen();
    const { zoomRange, isZoomed, resetZoom, selectionOverlayPlugin } = useChartDragZoom(
        chartRef,
        { enabled: !isNarrowScreen }
    );
    const effectiveSpanMs = useMemo(() => {
        const span = zoomRange
            ? Math.max(zoomRange.max - zoomRange.min, 0)
            : spanMs;
        if (!Number.isFinite(span) || span <= 0) return spanMs;
        return span;
    }, [zoomRange, spanMs]);
    // 根據螢幕寬度選擇適合的刻度配置
    const activeSpanMode = pickSpanModeForScreen(effectiveSpanMs, chartWidth);
    const safeMaxTicks = useMemo(() => {
        const maxTicks = chartWidth ? Math.floor(chartWidth / 50) : 12;
        return Math.max(4, Math.min(maxTicks, 20));
    }, [chartWidth]);
    const displayMaxTicks = useMemo(() => {
        if (!isNarrowScreen) return safeMaxTicks;
        return Math.min(safeMaxTicks, 6);
    }, [isNarrowScreen, safeMaxTicks]);
    const estimatedTicks = useMemo(() => {
        if (!Number.isFinite(effectiveSpanMs) || effectiveSpanMs <= 0) return 0;
        if (activeSpanMode.stepHours) {
            return effectiveSpanMs / (activeSpanMode.stepHours * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepDays) {
            return effectiveSpanMs / (activeSpanMode.stepDays * 24 * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepMonths) {
            return effectiveSpanMs / (activeSpanMode.stepMonths * 30 * 24 * 60 * 60 * 1000);
        }
        return 0;
    }, [effectiveSpanMs, activeSpanMode]);
    const shouldAutoSkip = estimatedTicks > safeMaxTicks;
    const tickLabelFormatter = useMemo(
        () => createTickLabelFormatter(activeSpanMode.mode),
        [activeSpanMode.mode]
    );
    const sparseTickFormatter = useMemo(() => {
        return (value, index, ticks) => {
            const total = ticks?.length || 0;
            const step = total > 0 ? Math.ceil(total / displayMaxTicks) : 1;
            if (step > 1 && index % step !== 0) return '';
            return tickLabelFormatter(value, index, ticks);
        };
    }, [displayMaxTicks, tickLabelFormatter]);
    const xMin = useMemo(() => {
        if (zoomRange?.min != null) return zoomRange.min;
        const first = points?.[0]?.x;
        if (first == null) return undefined;
        const dt = DateTime.fromMillis(first).setZone('Asia/Taipei');
        if (activeSpanMode.unit === 'day') return dt.startOf('day').toMillis();
        if (activeSpanMode.unit === 'hour') return dt.startOf('hour').toMillis();
        if (activeSpanMode.unit === 'minute') return dt.startOf('minute').toMillis();
        return first;
    }, [zoomRange?.min, points, activeSpanMode.unit]);
    const xMax = zoomRange?.max;

    // x 軸 time 設定 - 使用統一的時間刻度配置（必須在條件返回之前）
    const xTime = useMemo(() => {
        const baseConfig = createTimeScaleConfig(activeSpanMode);
        return {
            ...baseConfig,
            bounds: 'data',
            displayFormats: {
                millisecond: 'HH:mm:ss',
                second: 'HH:mm:ss',
                minute: 'HH:mm',
                hour: 'MM-dd HH:mm',
                day: 'M/d',
                month: 'yyyy-MM',
                year: 'yyyy',
            },
            tooltipFormat: 'yyyy/MM/dd HH:mm:ss',
        };
    }, [activeSpanMode]);

    const handleDownloadChart = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) return;

        // 自動根據數據時間範圍選擇適合的 scale
        const autoScale = getAutoScaleFromData(points);

        const timestamp = DateTime.local().setZone('Asia/Taipei').toFormat('yyyy_MM');

        downloadChartImage(chart, {
            fileNameBase: deviceName || 'alc-force',
            fileNameSuffix: timestamp,
            scale: autoScale,
            width: 800,
            height: 600,
        });
    }, [deviceName, points]);

    const dataRangeKey = useMemo(() => {
        const first = points[0]?.x;
        const last = points[points.length - 1]?.x;
        return `${first ?? 'na'}-${last ?? 'na'}-${points.length}`;
    }, [points]);

    useEffect(() => {
        resetZoom();
    }, [deviceId, dataRangeKey, resetZoom]);

    if (!points || points.length === 0) {
        return (
            <div className="chart-no-data">
                <p>暫無測量資料</p>
            </div>
        );
    }

    const mainDatasetLabelBase =
        `${deviceName ?? ''} ${CHART_CONSTANTS.LABELS.yAxis}`.trim();
    const mainDatasetLabel = mainDatasetLabelBase || CHART_CONSTANTS.LABELS.yAxis;

    const chartData = {
        datasets: [
            {
                label: mainDatasetLabel,
                data: points,
                borderColor: CHART_CONSTANTS.COLORS.main,
                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: CHART_CONSTANTS.COLORS.main,
                pointBorderColor: CHART_CONSTANTS.COLORS.main,
                pointBorderWidth: 0,
                pointHoverBackgroundColor: CHART_CONSTANTS.COLORS.main,
                spanGaps: true,
                yAxisID: 'y',
                parsing: false,
            },
            ...thresholds,
        ],
    };

    const stepY = yAxisConfig.stepSize || niceStep(0, 1, 6);

    // 根據行動值上限決定 step 和 max
    // action_up > 60 → step = 10
    // action_up <= 60 → step = 5
    // max 取最接近 action_up 的 step 倍數（向上取整）
    const finalStepY = actionUp != null ? (actionUp > 60 ? 10 : 5) : stepY;

    const suggestedMin = 0;
    const extremeMax =
        maxValue != null && Number.isFinite(maxValue) ? Math.max(0, maxValue) : null;
    const suggestedMax =
        extremeMax != null
            ? Math.max(finalStepY, Math.ceil(extremeMax / finalStepY) * finalStepY)
            : yAxisConfig.max != null
              ? yAxisConfig.max
              : suggestedMin + finalStepY * 6;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            crosshairY: {
                enabled: true,
                lineColor: 'rgba(0, 0, 0, 0.5)',
                lineWidth: 1,
                lineDash: [5, 5],
                showVerticalLine: true,
                showHorizontalLine: true,
                decimals: 0,
                unit: 'Ton',
            },
            title: {
                display: true,
                text: chartTitleText || '地錨荷重歷史趨勢',
                color: '#1f2937',
                font: { size: 16, weight: '600' },
                padding: { top: 10, bottom: 20 },
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    generateLabels: (chart) => {
                        const defaultLabels =
                            Chart.defaults.plugins.legend.labels.generateLabels(chart);
                        const datasets = chart.data.datasets || [];
                        const mainEntries = [];
                        const grouped = new Map();

                        defaultLabels.forEach((item) => {
                            const dataset = datasets[item.datasetIndex];
                            if (!dataset?._isThreshold) {
                                mainEntries.push(item);
                                return;
                            }

                            const groupKey = dataset.legendGroup || item.datasetIndex;
                            if (!grouped.has(groupKey)) {
                                grouped.set(groupKey, {
                                    ...item,
                                    text: dataset.legendLabel || dataset.label,
                                    strokeStyle: dataset.borderColor,
                                    lineDash: dataset.borderDash,
                                    lineWidth: dataset.borderWidth ?? 2,
                                    fillStyle:
                                        dataset.backgroundColor ??
                                        Chart.defaults.backgroundColor,
                                    _datasetIndices: [],
                                });
                            }
                            grouped.get(groupKey)._datasetIndices.push(item.datasetIndex);
                        });

                        const groupEntries = Array.from(grouped.values()).map((entry) => {
                            const hidden = entry._datasetIndices.every(
                                (idx) => !chart.isDatasetVisible(idx)
                            );
                            return {
                                ...entry,
                                hidden,
                                datasetIndex: entry._datasetIndices[0],
                            };
                        });

                        return [...mainEntries, ...groupEntries];
                    },
                },
                onClick: (event, legendItem, legend) => {
                    const chart = legend.chart;
                    const indices = legendItem._datasetIndices || [
                        legendItem.datasetIndex,
                    ];
                    const anyVisible = indices.some((idx) => chart.isDatasetVisible(idx));
                    indices.forEach((idx) =>
                        chart.setDatasetVisibility(idx, !anyVisible)
                    );
                    chart.update();
                },
            },
            tooltip: {
                callbacks: {
                    title: (items) =>
                        DateTime.fromMillis(items[0].parsed.x)
                            .setZone('Asia/Taipei')
                            .toFormat('yyyy/MM/dd HH:mm:ss'),
                    label: (ctx) => {
                        const val = ctx.parsed.y;
                        const label = ctx.dataset.label || '';
                        if (label.includes('警戒') || label.includes('行動')) {
                            return `${label}: ${val.toFixed(2)} Ton`;
                        }
                        return `${CHART_CONSTANTS.LABELS.yAxis}: ${val.toFixed(2)} Ton`;
                    },
                },
            },
            decimation: { enabled: true, algorithm: 'lttb', samples: 1000 },
        },
        scales: {
            x: {
                type: 'time',
                time: xTime,
                bounds: 'data',
                min: xMin,
                max: xMax,
                ticks: {
                    source: 'ticks',
                    autoSkip: shouldAutoSkip,
                    maxTicksLimit: displayMaxTicks,
                    autoSkipPadding: isNarrowScreen ? 6 : 12,
                    includeBounds: true,
                    maxRotation: 0,
                    minRotation: 0,
                    major: { enabled: true },
                    font: { size: isNarrowScreen ? 11 : 14, lineHeight: 1.2 },
                    padding: isNarrowScreen ? 6 : 12,
                    callback: sparseTickFormatter,
                },
                grid: { drawTicks: true },
                title: { display: true, text: '時間' },
            },
            y: {
                type: 'linear',
                min: suggestedMin,
                max: suggestedMax,
                title: { display: true, text: CHART_CONSTANTS.LABELS.yAxis },
                ticks: {
                    stepSize: finalStepY,
                    callback: (value) => {
                        const num = Number(value);
                        if (!Number.isFinite(num)) return '';
                        if (finalStepY < 1) return num.toFixed(1);
                        return Number.isInteger(num) ? num : num.toFixed(1);
                    },
                    format: { maximumFractionDigits: 1 },
                },
                grid: { drawTicks: true },
            },
        },
    };

    // ★ 使用統一的固定網格刻度插件
    const dynamicPlugins = [
        selectionOverlayPlugin,
        createFixedGridTicksPlugin(activeSpanMode),
        crosshairYPlugin,
    ];

    return (
        <div className="alc-chart-container" ref={containerRef}>
            <div className="chart-header">
                <div className="chart-actions">
                    {isZoomed && (
                        <button
                            type="button"
                            className="chart-reset-button"
                            onClick={resetZoom}
                        >
                            重置縮放
                        </button>
                    )}
                    {!isNarrowScreen && (
                        <button
                            type="button"
                            className="chart-download-button"
                            onClick={handleDownloadChart}
                        >
                            下載圖表
                        </button>
                    )}
                </div>
            </div>
            <div className="chart-wrapper">
                <Line
                    key={deviceId ? `alc-${deviceId}-${dataRangeKey}` : 'alc'}
                    ref={chartRef}
                    data={chartData}
                    options={options}
                    plugins={dynamicPlugins}
                />
            </div>
        </div>
    );
};

export default ALCChart;
